import { Card, Collapse, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import useUnitDeliveryTime from "@/hooks/use-unit-delivery-time";
import { CoverageAreaFiltersMessages } from "@/messages/coverage-area/coverage-area-filters.messages";
import { FilterType } from "@/types/filter.types";

import { CoverageAreaFilter, CoverageAreaFilterKey } from "../../types";

type Props = {
  messages: CoverageAreaFiltersMessages;
};

const CEP_CONFIG = {
  MASK: "99999-999",
  LENGTH: 8,
  FORMAT_REGEX: /(\d{5})(\d{3})/,
  FORMAT_REPLACEMENT: "$1-$2",
  NON_DIGIT_REGEX: /\D/g,
} as const;

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  MEDIUM: { lg: 6, md: 12, sm: 12, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<CoverageAreaFilter[]>();
  const [expanded, setExpanded] = useState<boolean>(false);
  const { regionalDeliveryTimeLabel } = useUnitDeliveryTime();

  const getValueFromProperty = (
    property: string,
    values: CoverageAreaFilter[]
  ): CoverageAreaFilter | undefined => {
    return values.find((f) => f.property === property);
  };

  const handleChange = (
    property: CoverageAreaFilterKey,
    value: string | number | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const filters = [...prevFilters];

      const existingIndex = filters.findIndex((f) => f.property === property);
      if (existingIndex !== -1) {
        filters.splice(existingIndex, 1);
      }

      const isEmpty = value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmpty) {
        return filters;
      }

      let valueToSet = value;
      switch (property) {
        case "startZipCode":
        case "endZipCode":
        case "servedZipCode":
          if (typeof value === "string") valueToSet = value.replace(CEP_CONFIG.NON_DIGIT_REGEX, "");
          break;
        case "freightChargeOrder":
        case "freightCostCarrier":
        case "startWeight":
        case "endWeight": {
          const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
          if (Number.isNaN(parsed)) return filters;
          if (
            (property === "freightChargeOrder" || property === "freightCostCarrier") &&
            parsed === 0
          ) {
            return filters;
          }
          valueToSet = parsed;
          break;
        }
        case "regionalDeliveryTime": {
          const parsed = typeof value === "number" ? value : Number.parseInt(String(value), 10);
          if (Number.isNaN(parsed)) return filters;
          valueToSet = parsed;
          break;
        }
        default:
          break;
      }

      const newFilter: CoverageAreaFilter = {
        property,
        filterType: filterType,
        value: valueToSet,
      };

      return [...filters, newFilter];
    });
  };

  const formatCep = (cep: string | number): string => {
    const cepString = String(cep).replace(CEP_CONFIG.NON_DIGIT_REGEX, "");
    return cepString.length === CEP_CONFIG.LENGTH
      ? cepString.replace(CEP_CONFIG.FORMAT_REGEX, CEP_CONFIG.FORMAT_REPLACEMENT)
      : cepString;
  };

  const renderTextField = (
    name: string,
    label: string,
    property: CoverageAreaFilterKey,
    filterType: FilterType = FilterType.Contains,
    type: string = "text"
  ) => (
    <TextField
      name={name}
      label={label}
      type={type}
      onChange={(e) => handleChange(property, e.target.value, filterType)}
      value={getValueFromProperty(property, values)?.value ?? ""}
      error={!!errors[0] && !!errors[0].value}
      helperText={!!errors[0] && errors[0].value}
      fullWidth
      variant="outlined"
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );

  const renderCurrencyInput = (
    name: string,
    label: string,
    property: CoverageAreaFilterKey,
    filterType: FilterType = FilterType.Equals
  ) => (
    <CurrencyInput
      name={name}
      label={label}
      value={getValueFromProperty(property, values)?.value ?? ""}
      variant="outlined"
      onChange={(e) => {
        const parsed = Number.parseFloat(e.target.value);
        handleChange(property, Number.isNaN(parsed) ? null : parsed, filterType);
      }}
      fullWidth
      error={!!errors[0]?.value}
      helperText={errors[0]?.value}
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );

  const renderCepInput = (
    name: string,
    label: string,
    property: CoverageAreaFilterKey,
    filterType: FilterType = FilterType.Equals
  ) => (
    <InputMaskWrapper
      mask={CEP_CONFIG.MASK}
      value={formatCep(String(getValueFromProperty(property, values)?.value ?? ""))}
      onChange={(e) => handleChange(property, e.target.value, filterType)}
      name={name}
      label={label}
      fullWidth
      variant="outlined"
      error={!!errors[0] && !!errors[0].value}
      helperText={!!errors[0] && errors[0].value}
      InputLabelProps={{ shrink: true }}
    />
  );

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE}>
            {renderCepInput("startZipCode", messages.startZipCodeLabel, "startZipCode")}
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            {renderCepInput("endZipCode", messages.endZipCodeLabel, "endZipCode")}
          </Grid>
          <Grid size={GRID_SIZES.MEDIUM}>
            {renderTextField("areaDescription", messages.areaDescriptionLabel, "areaDescription")}
          </Grid>

          <Grid size={12}>
            <Collapse in={expanded} unmountOnExit>
              <Grid container spacing={2}>
                <Grid size={GRID_SIZES.LARGE}>
                  {renderTextField(
                    "startWeight",
                    messages.startWeightLabel,
                    "startWeight",
                    FilterType.Equals,
                    "number"
                  )}
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  {renderTextField(
                    "endWeight",
                    messages.endWeightLabel,
                    "endWeight",
                    FilterType.Equals,
                    "number"
                  )}
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  {renderCurrencyInput(
                    "freightChargeOrder",
                    messages.freightChargeOrderLabel,
                    "freightChargeOrder"
                  )}
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  {renderCurrencyInput(
                    "freightCostCarrier",
                    messages.freightCostCarrierLabel,
                    "freightCostCarrier"
                  )}
                </Grid>
                <Grid size={GRID_SIZES.LARGE}>
                  {renderTextField(
                    "regionalDeliveryTime",
                    regionalDeliveryTimeLabel,
                    "regionalDeliveryTime",
                    FilterType.Equals,
                    "number"
                  )}
                </Grid>

                <Grid size={GRID_SIZES.LARGE}>
                  {renderTextField(
                    "freightType",
                    messages.freightTypeLabel,
                    "freightType",
                    FilterType.Contains
                  )}
                </Grid>

                <Grid size={GRID_SIZES.LARGE}>
                  {renderCepInput("servedZipCode", messages.servedZipCodeLabel, "servedZipCode")}
                </Grid>
              </Grid>
            </Collapse>

            <Grid container spacing={2} mt={expanded ? 2 : 0}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={() => setExpanded((prev) => !prev)}
                  fullWidth
                >
                  {expanded ? messages.lessFiltersButton : messages.moreFiltersButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={() => setValues([])} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
